import { dateFormat } from ".";
import { renderLocationCell } from "./tableFields";
import {
  formatCompactRelativeTimeAgo,
  formatWallClockDateCell,
  formatWallClockDateTimeCell,
} from "./wallClockTime";
import {
  MentorType,
  MenteeType,
  MenteesType,
  isVMSTheme,
  itemTitle,
  itemName,
  emailRegexString,
} from "../utils/constant";

export const formatPhoneNumber = (value) => {
  // Remove all non-numeric characters
  const numericValue = value.replace(/\D/g, "");

  // Format based on length
  if (numericValue.length <= 3) {
    return numericValue;
  } else if (numericValue.length <= 6) {
    return `(${numericValue.slice(0, 3)}) ${numericValue.slice(3)}`;
  } else {
    return `(${numericValue.slice(0, 3)}) ${numericValue.slice(
      3,
      6
    )}-${numericValue.slice(6, 10)}`;
  }
};

export const formatZipCode = (value) => {
  value = value.replace(/\D/g, "");

  if (value.length > 5) {
    value = value.slice(0, 5) + "-" + value.slice(5, 9);
  }

  value = value.slice(0, 10);

  return value;
};

export const formatLettersSpace = (value) => {
  const regex = new RegExp("[^A-Za-z\\s]", "g") ;
  return regex ? value.replace(regex, '') : value;
};

/** VIN: 17 chars, excludes I, O, Q per standard. */
export const formatVin = (value) =>
  String(value ?? "")
    .replace(/[^A-HJ-NPR-Za-hj-npr-z0-9]/g, "")
    .toUpperCase()
    .slice(0, 17);

export const formatInteger = (value) =>
  String(value ?? "").replace(/\D/g, "");

const PHONE_PATTERN_HINT = /\\d\{3\}/;

export const isPhonePattern = (pattern) =>
  typeof pattern === "string" && PHONE_PATTERN_HINT.test(pattern);

/**
 * Apply input formatting based on field config.
 * `is_pattern` alone enables HTML validation only — not phone formatting.
 */
export const formatFieldInputValue = (field, value) => {
  if (field?.format_type === "zip_code") return formatZipCode(value);
  if (field?.format_type === "isAlphaOnly") return formatLettersSpace(value);
  if (field?.format_type === "phone") return formatPhoneNumber(value);
  if (field?.format_type === "vin") return formatVin(value);
  if (field?.format_type === "integer") return formatInteger(value);

  if (field?.is_pattern && isPhonePattern(field?.pattern)) {
    return formatPhoneNumber(value);
  }

  return value;
};

export const INSIGHTS = {
  MyInsights: "myReports",
  MentorInsights: "mentorReports",
  EventMangerInsights: "eventManagerReports",
};

export const PersonalInformationFields = [
  {
    type: "input",
    name: "first_name",
    fieldtype: "text",
    label: "First Name",
    placeholder: "Enter First Name",
    inputRules: {
      required: "This field is required",
    },
    size: true,
    disable: true,
    widthQuestion: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
  },
  {
    type: "input",
    name: "last_name",
    fieldtype: "text",
    label: "Last Name",
    placeholder: "Enter Last Name",
    inputRules: {
      required: "This field is required",
    },
    size: true,
    disable: true,
    widthQuestion: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
  },
  {
    type: "input",
    name: "email",
    fieldtype: "email",
    label: "E-mail",
    placeholder: "Enter Email",
    inputRules: {
      required: "This field is required",
    },
    size: true,
    disable: true,
    widthQuestion: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
  },
  {
    type: "input",
    name: "phone_number",
    // fieldtype: 'number',
    label: "Primary Contact Number",
    placeholder: "Enter Primary Contact Number",
    inputRules: {
      required: "This field is required",
      pattern: {
        value: /^(\(\d{3}\) \d{3}-\d{4}|\d{3}-\d{3}-\d{4})$/,
        message: "Invalid phone number format",
      },
    },
    size: true,
    widthQuestion: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
  },
  {
    type: "input",
    name: "secondary_phone_number",
    // fieldtype: 'number',
    label: "Secondary Contact Number",
    placeholder: "Enter Secondary Number",
    inputRules: {
      // required: "This field is required",
      pattern: {
        value: /^(\(\d{3}\) \d{3}-\d{4}|\d{3}-\d{3}-\d{4})$/,
        message: "Invalid phone number format",
      },
    },
    size: true,
    widthQuestion: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
  },
  //  {
  //   type: "dropdown",
  //   name: "linkedin",
  //   label: "LinkedIn Profile (if available)",
  //   placeholder: "",
  //   options: [{
  //       key: "yes",
  //       value: "Yes"
  //     },
  //     {
  //       key: "no",
  //       value: "No"
  //     },
  //   ],
  //   inputRules: {
  //     // required: "This field is required",
  //   },
  //   size: true,
  // },
  {
    type: "input",
    name: "linked_in",
    fieldtype: "text",
    label: "LinkedIn Profile Link",
    placeholder: "Enter LinkedIn Profile Link",
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    widthQuestion: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
  },
  {
    type: "checkbox",
    name: "gender",
    label: "Gender",
    placeholder: "",
    options: [
      {
        key: "male",
        value: "Male",
      },
      {
        key: "female",
        value: "Female",
      },
      // {
      //   key: "others",
      //   value: "Others",
      // },
      {
        key: "nonbinary",
        value: "Nonbinary",
      },
      {
        key: "prefer not to answer",
        value: "Prefer not to answer",
      },
    ],
    inputRules: {
      // required: "This field is required",
    },
    size: true,
  },
  {
    type: "input",
    name: "location",
    fieldtype: "text",
    label: "Location",
    placeholder: "Enter Location",
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    widthQuestion: "w-full",
  },
];
export const AreaOfExpertiseFields = [
  {
    type: "input",
    name: "areas_of_expertise",
    fieldtype: "text",
    label: "List areas of expertise",
    placeholder: "(e.g., project management, software development, marketing)",
    inputRules: {
      // required: "This field is required",
    },
    size: false,
    widthQuestion: "w-full",
    width: "col-span-2 sm:col-span-2 md:col-span-2 lg:col-span-1 xl:col-span-1",
  },
  {
    type: "input",
    name: "confident_areas_of_expertise",
    fieldtype: "text",
    label: `What specific skills or knowledge are you most confident in sharing with a ${MenteeType.toLowerCase()}?`,
    placeholder: "Skills",
    inputRules: {
      // required: "This field is required",
    },
    size: false,
    widthQuestion: "w-full",
    width: "col-span-2 sm:col-span-2 md:col-span-2 lg:col-span-1 xl:col-span-1",
  },
];
export const EducationalBackgroundFields = [
  {
    type: "input",
    name: "highest_degree",
    fieldtype: "text",
    label: "Highest Degree Achieved",
    placeholder: "Highest Degree Achieved",
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    widthQuestion: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
    width: "col-span-4 sm:col-span-4 md:col-span-3 lg:col-span-2 xl:col-span-2",
  },
  {
    type: "input",
    name: "field_of_study",
    fieldtype: "text",
    label: "Field of Study",
    placeholder: "Field of Study",
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    widthQuestion: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
    width: "col-span-4 sm:col-span-4 md:col-span-3 lg:col-span-2 xl:col-span-2",
  },
  {
    type: "input",
    name: "institution_name",
    fieldtype: "text",
    label: "Institution Name",
    placeholder: "Institution Name",
    inputRules: {
      // required: 'This field is required',
    },
    size: false,
    widthQuestion: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
    width: "col-span-4 sm:col-span-4 md:col-span-3 lg:col-span-2 xl:col-span-2",
  },
  {
    type: "input",
    name: "institution_location",
    fieldtype: "text",
    label: "Institution Location",
    placeholder: "Institution Location",
    inputRules: {
      // required: 'This field is required',
    },
    size: false,
    widthQuestion: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
    width: "col-span-4 sm:col-span-4 md:col-span-3 lg:col-span-2 xl:col-span-2",
  },
];
export const MentorShipExperienceFields = [
  {
    type: "input",
    name: "years_of_experience",
    fieldtype: "number",
    label: "How many years of experience?",
    placeholder: "Enter year of experience",
    inputRules: {
      // required: "This field is required",
    },
    size: false,
    widthQuestion: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
    width: "col-span-2 sm:col-span-2 md:col-span-2 lg:col-span-1 xl:col-span-1",
  },
  {
    type: "radio",
    name: "prev_mentorship",
    label: "Have you previously mentored someone?",
    options: [
      {
        key: true,
        value: "Yes",
      },
      {
        key: false,
        value: "No",
      },
    ],
    inputRules: {
      // required: "This field is required",
    },
    size: false,
    widthQuestion: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
    width: "col-span-2 sm:col-span-2 md:col-span-2 lg:col-span-1 xl:col-span-1",
  },
  {
    type: "input",
    name: "mentor_exp_desc",
    fieldtype: "text",
    label: "If yes, please briefly describe your mentorship experience",
    placeholder: "mentorship experience",
    size: false,
    widthQuestion: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
    width: "col-span-2",
  },
];
export const MentorDocumentUplaod = [
  {
    type: "file",
    name: "documents",
    fieldtype: "file",
    label: "Documents Upload",
    placeholder: "",
    // inputRules: {
    //   required: "At least one file is required",
    // },
    size: false,
    widthQuestion: "w-full",
  },
];
export const ProfessionalBackgroundFields = [
  {
    type: "input",
    name: "job_title",
    fieldtype: "text",
    label: "Current Job Title",
    placeholder: "Current Job Title",
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    width: "col-span-4 sm:col-span-4 md:col-span-2 lg:col-span-2 xl:col-span-2",
  },
  {
    type: "input",
    name: "current_employer",
    fieldtype: "text",
    label: "Current Employer ",
    placeholder: "Current Employer ",
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    width: "col-span-4 sm:col-span-4 md:col-span-2 lg:col-span-2 xl:col-span-2",
  },
  {
    type: "input",
    name: "industry_type",
    fieldtype: "text",
    label: "Industry",
    placeholder: "Industry",
    inputRules: {
      // required: "This field is required",
    },
    size: false,
    width: "col-span-4",
  },
  // {
  //   type: 'input',
  //   name: 'linked_in',
  //   label: 'LinkedIn Profile (if available)',
  //   placeholder: 'LinkedIn Profile',

  //   inputRules: {
  //     // required: 'This field is required',
  //   },
  //   size: true,
  //   width: 'col-span-2',
  // },
];

export const MentorshipPreferenceFields = [
  {
    type: "input",
    name: "interested_mentee_type",
    fieldtype: "text",
    label: `What type of ${MenteeType.toLowerCase()} are you most interested in mentoring?`,
    placeholder:
      "(e.g., students, early-career professionals, career changers)",
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    width: "col-span-2 sm:col-span-2 md:col-span-2 lg:col-span-1 xl:col-span-1",
  },
  {
    type: "input",
    name: "communication_mode",
    fieldtype: "text",
    label: "Preferred method of communication",
    placeholder: "(e.g., email, video calls, in-person meetings)",
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    width: "col-span-2 sm:col-span-2 md:col-span-2 lg:col-span-1 xl:col-span-1",
  },
  {
    type: "input",
    name: "availability_frequency",
    fieldtype: "text",
    label: `How often are you available to meet with a ${MenteeType.toLowerCase()}?`,
    placeholder: "(e.g., weekly, bi-weekly, monthly)",
    inputRules: {
      // required: "This field is required",
    },
    size: false,
    width: "col-span-2",
  },
];

export const GoalsandExperienceFields = [
  {
    type: "input",
    name: "mentorship_achievement",
    fieldtype: "text",
    label: "What do you hope to achieve through this mentorship program?",
    placeholder: "",
    inputRules: {
      // required: "This field is required",
    },
    size: false,
    widthQuestion: "w-full lg:w-[48%] md:w-full xl:w-[48%]",
  },
  {
    type: "input",
    name: "mentor_expectations",
    fieldtype: "text",
    label: `What are your expectations from the ${MenteeType.toLowerCase()}?`,
    placeholder: "",
    inputRules: {
      // required: "This field is required",
    },
    size: false,
    widthQuestion: "w-full lg:w-[48%] md:w-full xl:w-[48%]",
  },
];

export const AvailabilityCommitmentFields = [
  {
    type: "input",
    name: `max_mentee_count`,
    fieldtype: "text",
    label: `How many ${MenteesType.toLowerCase()} are you willing to take on at a time?`,
    placeholder: "",
    inputRules: {
      // required: "This field is required",
    },
    size: false,
  },
  {
    type: "input",
    name: "pref_mentorship_duration",
    fieldtype: "text",
    label: "What is your preferred duration for a mentorship relationship?",
    placeholder: "",
    inputRules: {
      // required: "This field is required",
    },
    size: false,
  },
];

export const AdditionalInformationFields = [
  {
    type: "textbox",
    name: "additional_info",
    label:
      "Is there any additional information you would like to provide about yourself or your mentorship style?",
    placeholder: "",
    inputRules: {
      // required: "This field is required",
    },
  },
];

export const StepFormFields = [
  PersonalInformationFields,
  AreaOfExpertiseFields,
  EducationalBackgroundFields,
  MentorShipExperienceFields,
  GoalsandExperienceFields,
  MentorDocumentUplaod,
];

export const MenteePersonalInformationFields = [
  {
    type: "input",
    name: "first_name",
    fieldtype: "text",
    label: "First Name",
    placeholder: "Enter First Name",
    inputRules: {
      required: "This field is required",
    },
    size: true,
    disabled: true,
    disable: true,
    width: "col-span-6 sm:col-span-6 md:col-span-3 lg:col-span-2 xl:col-span-2",
    widthQuestion: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
  },
  {
    type: "input",
    name: "last_name",
    fieldtype: "text",
    label: "Last Name",
    placeholder: "Enter Last Name",
    inputRules: {
      required: "This field is required",
    },
    size: true,
    disabled: true,
    disable: true,
    width: "col-span-6 sm:col-span-6 md:col-span-3 lg:col-span-2 xl:col-span-2",
    widthQuestion: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
  },
  {
    type: "input",
    name: "email",
    fieldtype: "email",
    label: "E-mail",
    placeholder: "Enter Email",
    inputRules: {
      required: "This field is required",
    },
    size: true,
    disabled: true,
    disable: true,
    width: "col-span-6 sm:col-span-6 md:col-span-3 lg:col-span-2 xl:col-span-2",
    widthQuestion: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
  },
  {
    type: "input",
    name: "phone_number",
    // fieldtype: 'number',
    label: "Primary Contact Number",
    placeholder: "Primary Contact Number",
    inputRules: {
      required: "This field is required",
      pattern: {
        value: /^(\(\d{3}\) \d{3}-\d{4}|\d{3}-\d{3}-\d{4})$/,
        message: "Invalid phone number format",
      },
    },
    size: true,
    width: "col-span-6 sm:col-span-6 md:col-span-3 lg:col-span-2 xl:col-span-2",
    widthQuestion: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
  },
  {
    type: "input",
    name: "secondary_phone_number",
    // fieldtype: 'number',
    label: "Secondary Contact Number",
    placeholder: "Secondary Number",
    inputRules: {
      pattern: {
        value: /^(\(\d{3}\) \d{3}-\d{4}|\d{3}-\d{3}-\d{4})$/,
        message: "Invalid phone number format",
      },
    },
    size: true,
    width: "col-span-6 sm:col-span-6 md:col-span-3 lg:col-span-2 xl:col-span-2",
    widthQuestion: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
  },
  // {
  //   type: "input",
  //   name: "phone_number",
  //   // fieldtype: 'number',
  //   label: "Primary Contact Number",
  //   placeholder: "Enter Primary Contact Number",
  //   inputRules: {
  //     required: "This field is required",
  //     pattern: {
  //       value: /^(\(\d{3}\) \d{3}-\d{4}|\d{3}-\d{3}-\d{4})$/,
  //       message: "Invalid phone number format",
  //     },
  //   },
  //   size: true,
  //   width: "col-span-2",
  // },
  // {
  //   type: "input",
  //   name: "secondary_phone_number",
  //   // fieldtype: 'number',
  //   label: "Secondary Contact Number",
  //   placeholder: "Enter Secondary Contact Number",
  //   inputRules: {
  //     pattern: {
  //       value: /^(\(\d{3}\) \d{3}-\d{4}|\d{3}-\d{3}-\d{4})$/,
  //       message: "Invalid phone number format",
  //     },
  //   },
  //   size: true,
  //   width: "col-span-2",
  // },
  {
    type: "date",
    name: "dob",
    label: "Date Of Birth",
    placeholder: "Select Program Start Date and Time",
    inputRules: {
      // required: 'This field is required',
    },
    // disabled: true,
    size: true,
    width: "col-span-6 sm:col-span-6 md:col-span-3 lg:col-span-2 xl:col-span-2",
    widthQuestion: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
  },
  {
    type: "checkbox",
    name: "gender",
    label: "Gender",
    placeholder: "",
    options: [
      {
        key: "male",
        value: "Male",
      },
      {
        key: "female",
        value: "Female",
      },
      // {
      //   key: "others",
      //   value: "others",
      // },
      {
        key: "nonbinary",
        value: "Nonbinary",
      },
      {
        key: "prefer not to answer",
        value: "Prefer not to answer",
      },
    ],
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    width: "col-span-6 sm:col-span-6 md:col-span-6 lg:col-span-6 xl:col-span-2",
  },
  {
    type: "input",
    name: "location",
    fieldtype: "text",
    label: "Location",
    placeholder: "Enter Location",
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    width: "col-span-6 sm:col-span-6 md:col-span-3 lg:col-span-3 xl:col-span-2",
    widthQuestion: "w-full",
  },
  {
    type: "dropdown",
    name: "current_education",
    label: "Current Education level",
    placeholder: "",
    options: [
      {
        key: "UG",
        value: "Under Graduate",
      },
      {
        key: "HSC",
        value: "Higher Secondary",
      },
      {
        key: "PG",
        value: "Post Graduate",
      },
      {
        key: "PHD",
        value: "Doctorate/Ph.D",
      },
    ],
    inputRules: {
      required: "This field is required",
    },
    size: false,
    width: "col-span-6 sm:col-span-6 md:col-span-3 lg:col-span-3 xl:col-span-2",
    widthQuestion: "w-full",
  },
];

export const MenteeCareerInformationFields = [
  {
    type: "input",
    name: "career_goals",
    fieldtype: "text",
    label: "What are your current academic or career goals?",
    placeholder: "Enter career goals",
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    width: "col-span-2 sm:col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1",
    widthQuestion: "w-full lg:w-[48%] md:w-full xl:w-[48%]",
  },
  {
    type: "input",
    name: "gain_skills",
    fieldtype: "text",
    label: `What specific skills or knowledge do you hope to gain from a ${MentorType.toLowerCase()}?`,
    placeholder: "Enter skills or knowledge",
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    width: "col-span-2 sm:col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1",
    widthQuestion: "w-full lg:w-[48%] md:w-full xl:w-[48%]",
  },
  {
    type: "input",
    name: "fields_of_interest",
    fieldtype: "text",
    label: "What field or industry are you interested in?",
    placeholder: "Enter field or industry ",
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    width: "col-span-2 sm:col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1",
    widthQuestion: "w-full lg:w-[48%] md:w-full xl:w-[48%]",
  },
  {
    type: "dropdown",
    name: "expectations_of_future",
    label: "Where do you see yourself in 5 years?",
    placeholder: "",
    options: [
      {
        key: "option1",
        value: "Option 1",
      },
      {
        key: "option2",
        value: "Option 2",
      },
      {
        key: "option3",
        value: "Option 3",
      },
    ],
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    width: "col-span-2 sm:col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1",
    widthQuestion: "w-full lg:w-[48%] md:w-full xl:w-[48%]",
  },
];

export const MenteeCurrentStatusFields = [
  {
    type: "input",
    name: "specialization_study",
    fieldtype: "text",
    label: "What is your current major or field of study (if applicable)?",
    placeholder: "Enter current major or field",
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    width: "col-span-2 sm:col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1",
    widthQuestion: "w-full lg:w-[48%] md:w-full xl:w-[48%]",
  },
  {
    type: "input",
    name: "work_experience",
    fieldtype: "text",
    label: "Do you have any work experience? If so, please describe.",
    placeholder: "Enter work experience",
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    width: "col-span-2 sm:col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1",
    widthQuestion: "w-full lg:w-[48%] md:w-full xl:w-[48%]",
  },
  {
    type: "input",
    name: "extra_curricular_activities",
    fieldtype: "text",
    label:
      "Are you currently involved in any extracurricular activities, clubs, or organizations?",
    placeholder: "Enter extracurricular activities",
    inputRules: {
      // required: "This field is required",
    },
    size: false,
    width: "col-span-2 sm:col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1",
    widthQuestion: "w-full lg:w-[48%] md:w-full xl:w-[48%]",
  },
];

export const MenteeMentoringPreferenceFields = [
  {
    type: "dropdown",
    name: "mentoring_expectations",
    label: "What type of mentoring are you looking for? ",
    placeholder: "",
    options: [
      {
        key: "option1",
        value: "Option 1",
      },
    ],
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    width: "col-span-2 sm:col-span-2 md:col-span-2 lg:col-span-1 xl:col-span-1",
    widthQuestion: "w-full lg:w-[48%] md:w-full xl:w-[48%]",
  },
  {
    type: "dropdown",
    name: "mentor_meet_frequency",
    label: `How often would you like to meet with your ${MentorType.toLowerCase()}?`,
    placeholder: "",
    options: [
      {
        key: "option1",
        value: "Option 1",
      },
    ],
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    width: "col-span-2 sm:col-span-2 md:col-span-2 lg:col-span-1 xl:col-span-1",
    widthQuestion: "w-full lg:w-[48%] md:w-full xl:w-[48%]",
  },
  {
    type: "dropdown",
    name: "mentor_communication_method",
    label: `What is your preferred method of communication with your  ${MentorType.toLowerCase()}?`,
    placeholder: "",
    options: [
      {
        key: "option1",
        value: "Option 1",
      },
    ],
    inputRules: {
      // required: "This field is required",
    },
    size: false,
    width: "col-span-2",
    widthQuestion: "w-full lg:w-[48%] md:w-full xl:w-[48%]",
  },
];

export const MenteeSkillsFields = [
  {
    type: "input",
    name: "strongest_skills",
    fieldtype: "text",
    label: "What are your strongest skills?",
    placeholder: "Strongest skills?",
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    width: "col-span-2 sm:col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1",
  },
  {
    type: "input",
    name: "areas_of_improvement",
    fieldtype: "text",
    label: "What areas do you feel you need the most improvement in?",
    placeholder: "You need the most improvement in",
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    width: "col-span-2 sm:col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1",
  },
  {
    type: "input",
    name: "related_hobbies_interests",
    fieldtype: "text",
    label: "What are your hobbies or interests outside of academics or work?",
    placeholder: "Your hobbies or interests outside of academics or work?",
    inputRules: {
      // required: "This field is required",
    },
    size: false,
    width: "col-span-2 sm:col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1",
  },
];

export const MenteeExpectationGoalsFields = [
  {
    type: "input",
    name: "expectations_mentoring_relationship",
    fieldtype: "text",
    label: "What are your expectations from your mentoring relationship?",
    placeholder: "Your expectations from your mentoring relationship",
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    width: "col-span-2 sm:col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1",
  },
  {
    type: "input",
    name: "goals_from_mentorship",
    fieldtype: "text",
    label:
      "Do you have any specific goals you want to achieve through this mentorship?",
    placeholder:
      "you have any specific goals you want to achieve through this mentorship",
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    width: "col-span-2 sm:col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1",
  },
  {
    type: "input",
    name: `mentee_thoughts_mentor`,
    fieldtype: "text",
    label: `Is there anything specific you would like your ${MentorType.toLowerCase()} to know about you?`,
    placeholder: `Is there anything specific you would like your ${MentorType.toLowerCase()} to know about you`,
    inputRules: {
      // required: "This field is required",
    },
    size: false,
    width: "col-span-2 sm:col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1",
  },
];

export const MenteeAvailabilitysFields = [
  {
    type: "input",
    name: "days_times_for_meetings",
    fieldtype: "text",
    label: "What days and times are you typically available for meetings?",
    placeholder: "Days and times are you typically available for meetings",
    inputRules: {
      // required: "This field is required",
    },
    size: false,
    width: "col-span-2 sm:col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1",
  },
  {
    type: "input",
    name: "available_for_virtual_meeting",
    fieldtype: "text",
    label:
      "Are you open to virtual meetings if in-person meetings are not possible?",
    placeholder:
      "Are you open to virtual meetings if in-person meetings are not possible?",
    inputRules: {
      // required: "This field is required",
    },
    size: false,
    width: "col-span-2 sm:col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1",
  },
];

export const MenteeDetailedCareerFields = [
  {
    type: "input",
    name: "inspiration_for_current_field",
    fieldtype: "text",
    label: "What inspired you to pursue your current field or major?",
    placeholder: "inspired you to pursue your current field or major",
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    width: "col-span-2 sm:col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1",
  },
  {
    type: "input",
    name: "projects_research_topics",
    fieldtype: "text",
    label:
      "Are there any specific projects or research topics you are passionate about?",
    placeholder:
      "Are there any specific projects or research topics you are passionate about?",
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    width: "col-span-2 sm:col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1",
  },
  {
    type: "input",
    name: "dreams_career_path",
    fieldtype: "text",
    label: "What is your dream job or career path?",
    placeholder: "Your dream job or career path",
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    width: "col-span-2 sm:col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1",
  },
  {
    type: "input",
    name: "preferred_companies_to_work",
    fieldtype: "text",
    label:
      "Have you identified any specific companies or organizations you would like to work for in the future?",
    placeholder:
      "Have you identified any specific companies or organizations you would like to work for in the future?",
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    width: "col-span-2 sm:col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1",
  },
];

export const MenteeChallengesFields = [
  {
    type: "input",
    name: "current_challenges_faced",
    fieldtype: "text",
    label:
      "What are the biggest challenges you are currently facing in your academic or career journey?",
    placeholder:
      "The biggest challenges you are currently facing in your academic or career journey",
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    width: "col-span-2 sm:col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1",
  },
  {
    type: "input",
    name: "obstacles_encountered",
    fieldtype: "text",
    label:
      "Have you encountered any obstacles that have hindered your progress?",
    placeholder:
      "Have you encountered any obstacles that have hindered your progress?",
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    width: "col-span-2 sm:col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1",
  },
  {
    type: "input",
    name: "handle_setbacks",
    fieldtype: "text",
    label: "How do you typically handle setbacks or failures?",
    placeholder: "You typically handle setbacks or failures",
    inputRules: {
      // required: "This field is required",
    },
    size: false,
    width: "col-span-2 sm:col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1",
  },
];

export const MenteeMentoringExperienceFields = [
  {
    type: "input",
    name: "past_mentoring_exp",
    fieldtype: "text",
    label: `Have you had a ${MentorType.toLowerCase()} before? If so, what was your experience like?`,
    placeholder: `Have you had a ${MentorType.toLowerCase()} before? If so, what was your experience like`,
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    width: "col-span-2 sm:col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1",
  },
  {
    type: "input",
    name: "values_from_past_mentoring_exp",
    fieldtype: "text",
    label:
      "What did you find most valuable about your previous mentoring experience?",
    placeholder:
      "You find most valuable about your previous mentoring experience",
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    width: "col-span-2 sm:col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1",
  },
  {
    type: "input",
    name: "mentoring_relationship_expectations",
    fieldtype: "text",
    label:
      "What would you like to be different in this mentoring relationship compared to previous ones?",
    placeholder:
      "Would you like to be different in this mentoring relationship compared to previous ones",
    inputRules: {
      // required: "This field is required",
    },
    size: false,
    width: "col-span-2 sm:col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1",
  },
];

export const MenteeLearningFields = [
  {
    type: "dropdown",
    name: "feedback_preference",
    label: "How do you prefer to receive feedback and guidance?",
    placeholder: "",
    options: [
      {
        key: "option1",
        value: "Option 1",
      },
      {
        key: "option2",
        value: "Option 2",
      },
    ],
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    width: "col-span-2 sm:col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1",
  },
  {
    type: "input",
    name: "learning_style_preference",
    fieldtype: "text",
    label: "What is your preferred learning style?",
    placeholder: "Your preferred learning style",
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    width: "col-span-2 sm:col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1",
  },
  {
    type: "input",
    name: "structured_sessions_preference",
    fieldtype: "text",
    label:
      "Do you prefer structured sessions with specific topics, or more informal, flexible discussions?",
    placeholder:
      "You prefer structured sessions with specific topics, or more informal, flexible discussions",
    inputRules: {
      // required: "This field is required",
    },
    size: false,
    width: "col-span-2 sm:col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1",
  },
];

export const MenteeNetworkingFields = [
  {
    type: "input",
    name: "networking_opportunities_prefs",
    fieldtype: "text",
    label: `Are you interested in networking opportunities through your ${MentorType.toLowerCase()}?`,
    placeholder: `Are you interested in networking opportunities through your ${MentorType.toLowerCase()}?`,
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    width: "col-span-2 sm:col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1",
  },
  {
    type: "input",
    name: "assistance_req",
    fieldtype: "text",
    label:
      "Do you need assistance with resume writing, interview preparation, or job search strategies?",
    placeholder: "Your preferred learning style",
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    width: "col-span-2 sm:col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1",
  },
  {
    type: "input",
    name: "professional_resources_prefs",
    fieldtype: "text",
    label:
      "Would you like to learn about professional development resources such as workshops, seminars, or conferences?",
    placeholder:
      "Would you like to learn about professional development resources such as workshops, seminars, or conferences",
    inputRules: {
      // required: "This field is required",
    },
    size: false,
    width: "col-span-2 sm:col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1",
  },
];

export const MenteePersonalDevelopmentFields = [
  {
    type: "input",
    name: "develop_skills_quality_prefs",
    fieldtype: "text",
    label:
      "What personal qualities or skills would you like to develop further?",
    placeholder:
      "Personal qualities or skills would you like to develop further?",
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    width: "col-span-2 sm:col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1",
  },
  {
    type: "input",
    name: "personal_challenges_overcoming",
    fieldtype: "text",
    label:
      "Are there any specific personal challenges you are working on overcoming?",
    placeholder:
      "Are there any specific personal challenges you are working on overcoming?",
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    width: "col-span-2 sm:col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1",
  },
  {
    type: "input",
    name: "stress_management_methods",
    fieldtype: "text",
    label: "How do you manage stress and maintain a healthy work-life balance?",
    placeholder:
      "Would you like to learn about professional development resources such as workshops, seminars, or conferences",
    inputRules: {
      // required: "This field is required",
    },
    size: false,
    width: "col-span-2 sm:col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1",
  },
];

export const MenteeMentoringRelationshipFields = [
  {
    type: "input",
    name: "mentor_qualities",
    fieldtype: "text",
    label: `What qualities are you looking for in a ${MentorType.toLowerCase()}?`,
    placeholder: `What qualities are you looking for in a ${MentorType.toLowerCase()}?`,
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    width: "col-span-2 sm:col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1",
  },
  {
    type: "input",
    name: "interested_mentoring_activities",
    fieldtype: "text",
    label:
      "Are there any specific mentoring activities or exercises you are interested in?",
    placeholder:
      "Are there any specific mentoring activities or exercises you are interested in?",
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    width: "col-span-2 sm:col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1",
  },
  {
    type: "input",
    name: "measures_mentoring_relationship",
    fieldtype: "text",
    label: "How do you measure success in a mentoring relationship?",
    placeholder: "You measure success in a mentoring relationship",
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    width: "col-span-2 sm:col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1",
  },
  {
    type: "input",
    name: "mentor_guidance_from_distance",
    fieldtype: "text",
    label: `Do you prefer a ${MentorType.toLowerCase()} who takes a hands-on approach or one who offers guidance from a distance?`,
    placeholder: `You prefer a ${MentorType.toLowerCase()} who takes a hands-on approach or one who offers guidance from a distance`,
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    width: "col-span-2 sm:col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1",
  },
];

export const MenteeLongTermFields = [
  {
    type: "input",
    name: "long_term_aspirations",
    fieldtype: "text",
    label: "What are your long-term personal and professional aspirations?",
    placeholder: "Your long-term personal and professional aspirations",
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    width: "col-span-2 sm:col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1",
    widthQuestion: "w-full lg:w-[48%] md:w-full xl:w-[48%]",
  },
  {
    type: "input",
    name: "plans_giveback_community",
    fieldtype: "text",
    label:
      "How do you plan to give back to your community or field in the future?",
    placeholder:
      "You plan to give back to your community or field in the future",
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    width: "col-span-2 sm:col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1",
    widthQuestion: "w-full lg:w-[48%] md:w-full xl:w-[48%]",
  },
  {
    type: "input",
    name: "legacy_from_life",
    fieldtype: "text",
    label:
      "What legacy do you want to leave behind in your career or personal life?",
    placeholder:
      "legacy do you want to leave behind in your career or personal life",
    inputRules: {
      // required: "This field is required",
    },
    size: false,
    width: "col-span-2 sm:col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1",
    widthQuestion: "w-full lg:w-[48%] md:w-full xl:w-[48%]",
  },
];
export const MenteeDocumentUplaod = [
  {
    type: "file",
    name: "documents",
    fieldtype: "file",
    label: "Documents Upload",
    placeholder: "",
    // inputRules: {
    //   required: "This field is required",
    // },
    size: false,
    widthQuestion: "w-full",
  },
];

export const MenteeAdditionalFields = [
  {
    type: "input",
    name: "additional_information",
    fieldtype: "text",
    label: `Is there any additional information you would like to share that could help in matching you with a ${MentorType.toLowerCase()}?`,
    placeholder: `Is there any additional information you would like to share that could help in matching you with a ${MentorType.toLowerCase()}`,
    inputRules: {
      // required: "This field is required",
    },
    size: false,
    width: "col-span-1",
  },
  {
    type: "input",
    name: "request_need_mentor_to_know",
    fieldtype: "text",
    label:
      "How do you plan to give back to your community or field in the future?",
    placeholder:
      "You plan to give back to your community or field in the future",
    inputRules: {
      // required: "This field is required",
    },
    size: false,
    width: "col-span-1",
  },
];

export const MenteeStepFormFields = [
  MenteePersonalInformationFields,
  MenteeCareerInformationFields,
  MenteeCurrentStatusFields,
  MenteeMentoringPreferenceFields,
  MenteeLongTermFields,
  MenteeDocumentUplaod,
];

export const Stepname = [
  "personal_information",
  "area_of_expertise",
  "educational_background",
  "mentor_ship_experience",
  "goals_expectations",
  "document_upload",
];

export const MenteeStepname = [
  "personal_information",
  "current_status",
  "skills_and_interests",
  "expectations_goals",
  "long_term_vision",
  "document_upload",
];

export const ProgramTabs = [
  {
    name: "Program Information",
    key: "program_information",
  },
  {
    name: "About Program",
    key: "about_program",
  },
];

export const CourseLevelOptions = [
  {
    key: "beginner",
    value: "Beginner",
  },
  {
    key: "intermediate",
    value: "Intermediate",
  },
  {
    key: "expert",
    value: "Expert",
  },
];

export const SessionCounts = () => {
  const sessions = [];
  for (let n = 1; n <= 20; n++) {
    sessions.push({
      key: n,
      value: n,
    });
  }
  return sessions;
};

// [
//   {
//     question: "test",
//     mandatory: "true",
//     field_type: "radio",
//     field_options: { option1: "yes", option2: "no" },
//   },
//   {
//     question: "test2",
//     mandatory: "true",
//     field_type: "radio",
//     field_options: { option1: "yes", option2: "no" },
//   },
// ];

export const DEFAULT_STATE = "CA";
export const LOCATION_SEARCH_PARAMS = { state: DEFAULT_STATE };

export const normalizeCategories = (categories) =>
  Array.isArray(categories) ? categories : categories?.results ?? [];

export const isParentLevelSpecialty = (category) =>
  category &&
  category.is_subcategory === false &&
  String(category?.name).toLowerCase() !== "general";

export const getCategoryById = (categoryId, categories = []) => {
  const normalized = normalizeCategories(categories);
  const directMatch = normalized.find(
    (category) => String(category?.id) === String(categoryId)
  );
  if (directMatch) return directMatch;

  for (const category of normalized) {
    const subcategory = (category?.subcategories ?? []).find(
      (item) => String(item?.id) === String(categoryId)
    );
    if (subcategory) return subcategory;
  }

  return null;
};

export const getStaffSpecialtyDisplay = (
  specialties = [],
  categories = []
) => {
  const uniqueSpecialties = [...new Set(
    (Array.isArray(specialties) ? specialties : [])
    .map((item) => {
      if (item?.parent) {
        return (
          item?.parent_name ||
          getCategoryById(item.parent, categories)?.name ||
          "-"
        );
      }
      return item?.name || "-";
    })
    .filter((value) => value && value !== "-")
  ),
]

  return uniqueSpecialties.length ? uniqueSpecialties.join(", ") : "-";
};

export const getStaffSubSpecialtyDisplay = (specialties = []) => {
  const values = (Array.isArray(specialties) ? specialties : [])
    .map((item) => (item?.parent ? item?.name : null))
    .filter(Boolean);

  return values.length ? values.join(", ") : "-";
};

export const isMedicalSpecialty = (specialtyId, categories = []) =>
  String(getCategoryById(specialtyId, categories)?.name).toLowerCase() ===
  "medical";

export const isDentalSpecialty = (specialtyId, categories = []) =>
  String(getCategoryById(specialtyId, categories)?.name).toLowerCase() ===
  "dental";

export const getAssignMedicalAssistantFieldText = (
  specialtyId,
  categories = [],
  programType = "program"
) => {
  const isDental = isDentalSpecialty(specialtyId, categories);

  if (isDental) {
    return {
      label: "Assign Dental assistant",
      placeholder: "Assign dental assistant",
      tooltip: `Select a dental assistant to support this ${programType}.`,
      roleLabel: "Dental Assistant",
      neededLabel: "Dental Assistant needed",
    };
  }

  return {
    label: "Assign Medical Assistant",
    placeholder: "Assign Medical Assistant",
    tooltip: `Select a medical assistant to support this ${programType}.`,
    roleLabel: "Medical Assistant",
    neededLabel: "Medical Assistant needed",
  };
};

export const getMedicalCategory = (categories = []) =>
  normalizeCategories(categories).find(
    (category) => String(category?.name).toLowerCase() === "medical"
  );

export const buildSpecialtyOptions = (categories = []) =>
  normalizeCategories(categories)
    .filter(isParentLevelSpecialty)
    .map((category) => ({
      id: category.id,
      name: category.name,
      value: category.name,
    }));

export const buildSubSpecialtyOptions = (categories = []) =>
  (getMedicalCategory(categories)?.subcategories ?? []).map((category) => ({
    id: category.id,
    name: category.name,
    value: category.name,
  }));

export const buildCategoryPayloadValue = (
  specialty,
  subSpecialty,
  categories = []
) =>
  isMedicalSpecialty(specialty, categories) && subSpecialty
    ? subSpecialty
    : specialty;

export const resolveCategoryFormState = (apiCategoryId, categories = []) => {
  const medicalCategory = getMedicalCategory(categories);
  const medicalSubCategory = (medicalCategory?.subcategories ?? []).find(
    (category) => String(category?.id) === String(apiCategoryId)
  );

  if (medicalSubCategory) {
    return {
      specialty: medicalCategory?.id ?? "",
      sub_specialty: medicalSubCategory.id,
      category: medicalSubCategory.id,
    };
  }

  return {
    specialty: apiCategoryId ?? "",
    sub_specialty: "",
    category: apiCategoryId ?? "",
  };
};

export const EDITABLE_AFTER_COMPLETION_FIELDS = [
  "patient_goal",
  "scheduled_count",
  "completed_count",
  "no_show_count",
  "no_of_walk_in",
  "cancellation_count",
  "reschedule_count",
  "billable_visits"
];

export const getProgramInformationFields = (
  programType = "program",
  is_event = false,
  isGridPolarisTrainingProgram = false,
  currentStatus = null
) => {
  // Helper function to replace "program" with the provided type in labels and tooltips
  const replaceTermInText = (
    text,
    oldTerm = "program",
    newTerm = programType
  ) => {
    if (!text) return text;

    // Handle case-sensitive replacements (Program, program)
    const regexLowerCase = new RegExp(oldTerm, "gi");
    const replacer = (match) => {
      // Keep the same case pattern as the original match
      if (match === match.toLowerCase()) return newTerm.toLowerCase();
      if (match === match.toUpperCase()) return newTerm.toUpperCase();
      if (
        match ===
        match.charAt(0).toUpperCase() + match.slice(1).toLowerCase()
      ) {
        return newTerm.charAt(0).toUpperCase() + newTerm.slice(1).toLowerCase();
      }
      return newTerm;
    };

    return text.replace(regexLowerCase, replacer);
  };


  const isCompletedEvent = currentStatus === "completed";

  const integerInputRules = {
    pattern: {
      value: /^\d*$/,
      message: "Only numbers are allowed",
    },
  };

  const patientVisitSummaryFields = [
    {
      type: "input",
      name: "scheduled_count",
      label: "Patients Scheduled",
      placeholder: "Enter scheduled patient count",
      fieldtype: "text",
      format_type: "integer",
      newRow: true,
      inputRules: integerInputRules,
      width: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
      tooltip: "Number of patients scheduled for the event",
    },
    {
      type: "input",
      name: "completed_count",
      label: "Patients Seen",
      placeholder: "Enter patients seen count",
      fieldtype: "text",
      format_type: "integer",
      inputRules: integerInputRules,
      width: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
      tooltip: "Number of patients who attended and received services",
    },
    {
      type: "input",
      name: "no_show_count",
      label: "No Show patients",
      placeholder: "Enter No Show patient count",
      fieldtype: "text",
      format_type: "integer",
      inputRules: integerInputRules,
      width: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
      tooltip: "Number of registered patients who did not attend the event",
    },
    {
      type: "input",
      name: "no_of_walk_in",
      label: "Walk In Patients",
      placeholder: "Enter Walk In patient count",
      fieldtype: "text",
      format_type: "integer",
      inputRules: integerInputRules,
      width: "w-full lg:w-[24%] md:w-[48%] xl:w-[24%]",
      tooltip: "Number of patients who attended without prior registration",
    },
    {
      type: "input",
      name: "cancellation_count",
      label: "Same Day Cancellations",
      placeholder: "Enter same day cancellation count",
      fieldtype: "text",
      format_type: "integer",
      inputRules: integerInputRules,
      width: "w-full lg:w-[30%] md:w-[48%] xl:w-[24%]",
      tooltip: "Number of appointments cancelled on the day of the event",
    },
    {
      type: "input",
      name: "reschedule_count",
      label: "Same Day Reschedules",
      placeholder: "Enter same day reschedule count",
      fieldtype: "text",
      format_type: "integer",
      inputRules: integerInputRules,
      width: "w-full lg:w-[30%] md:w-[48%] xl:w-[24%]",
      tooltip: "Number of appointments rescheduled on the day of the event",
    },
     {
      type: "input",
      name: "billable_visits",
      label: "Billable Visits",
      placeholder: "Enter billable visit count",
      fieldtype: "text",
      format_type: "integer",
      // newRow: true,
      inputRules: integerInputRules,
      width: "w-full lg:w-[24%] md:w-[48%] xl:w-[24%]",
      tooltip: "Number of patients billable visits for the event",
    },
  ];

  const programInformationOptions = [
    {
      type: "input",
      name: "program_name",
      fieldtype: "text",
      label: "Organization Name",
      placeholder: "Enter Organization Name",
      inputRules: {
        required: "This field is required",
        maxLength: 255,
      },
      width: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
      for: ["mentor"],
      tooltip: replaceTermInText(
        `Enter a descriptive name for the Organization`
      ),
    },
    {
      type: "input",
      name: "host_name",
      fieldtype: "text",
      label: "Host Name",
      placeholder: "Enter Host Name",
      width: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
      tooltip: replaceTermInText('Enter the name of the site hosting this event')
    },
    {
      type: "input",
      name: "host_email",
      fieldtype: "email",
      label: "Host Email",
      placeholder: "Enter Host Email",
      inputRules: {
        pattern: {
          value: emailRegexString,
          message: "Invalid email address",
        },
      },
      width: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
      tooltip: replaceTermInText('Enter the email address of the host organization or event contact')
    },
    {
      type: "input",
      name: "host_phone_number",
      fieldtype: "text",
      label: "Host Phone Number",
      placeholder: "Enter Host Phone Number",
      format_type: "phone",
      inputRules: {
        pattern: {
          value: /^(\(\d{3}\) \d{3}-\d{4}|\d{3}-\d{3}-\d{4})$/,
          message: "Phone number must be exactly 10 digits",
        },
      },
      width: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
      tooltip: replaceTermInText('Enter the phone number of the host organization or event contact')
    },
    {
      type: "dropdown",
      name: "specialty",
      label: "Specialty",
      placeholder: "Select Specialty",
      inputRules: {
        required: "This field is required",
      },
      options: [],
      width: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
      for: ["admin", "mentor"],
      tooltip: replaceTermInText(
        `Select the relevant specialty this ${itemName} belongs to`
      ),
    },
    {
      type: "dropdown",
      name: "sub_specialty",
      label: "SubSpecialty",
      placeholder: "Select SubSpecialty",
      showForMedicalSpecialty: true,
      inputRules: {
        required: "This field is required",
      },
      options: [],
      width: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
      for: ["admin", "mentor"],
      tooltip: replaceTermInText(
        `Select the relevant sub specialty for this medical ${itemName}`
      ),
    },
    // { ...typeOfProgramField },
    {
      type: "date",
      name: "start_date",
      label: "Start Date",
      placeholder: "Select Start Date",
      inputRules: {
        required: "This field is required",
      },
      width: "w-full lg:w-[24%] md:w-[48%] xl:w-[24%]",
      newRow: true,
      for: ["admin"],
      tooltip: replaceTermInText(`Select the date the ${itemName} begins`),
    },
    {
      type: "date",
      name: "end_date",
      label: "End Date",
      placeholder: "Select End Date",
      inputRules: {
        required: "This field is required",
      },
      width: "w-full lg:w-[30%] md:w-[48%] xl:w-[24%]",
      for: ["admin"],
      tooltip: replaceTermInText(`Select the date the ${itemName} ends`),
    },
    {
      type: "dropdown",
      name: "is_private",
      label: "Visibility",
      placeholder: "Select Visibility",
      options: [
        { key: "false", value: "Public" },
        { key: "true", value: "Private" },
      ],
      inputRules: {},
      width: "w-full lg:w-[30%] md:w-[48%] xl:w-[24%]",
      for: ["admin"],
      tooltip: "Select whether this event is public or private",
    },
    {
      type: "dropdown",
      name: "is_billable",
      label: "Billing Type",
      placeholder: "Select Billing Type",
      options: [
        { key: "true", value: "Billable" },
        { key: "false", value: "Non Billable" },
      ],
      inputRules: {},
      width: "w-full lg:w-[24%] md:w-[48%] xl:w-[24%]",
      for: ["admin"],
      tooltip: "Select whether this event is billable or non billable",
    },
    {
      type: "program_frequency",
      name: "reminder_type",
      label: "Frequency",
      placeholder: "",
      options: [
        {
          key: "daily",
          value: "Daily",
          tooltip: "Repeats every day at the same time",
        },
        {
          key: "weekly_byday",
          value: "Weekly",
          tooltip: "Repeats on the same day/days every week",
        },
        {
          key: "month_week",
          value: "Monthly",
          tooltip: "Repeats on the same date/dates every month",
        },
      ],
      inputRules: {
        required: "This field is required",
      },
      size: true,
      width: "w-full",
      for: ["mentor"],
      tooltip: replaceTermInText(
        `Select how often sessions are held during the ${itemName}`
      ),
    },
    {
      type: "input",
      name: "patient_goal",
      label: "Patient Goal",
      placeholder: "Enter patient goal",
      fieldtype: "text",
      format_type: "integer",
      inputRules: {
        required: "This field is required",
        pattern: {
          value: /^\d+$/,
          message: "Only whole numbers are allowed",
        },
        validate: (value) => {
          if (value === "" || value === null || value === undefined) {
            return "This field is required";
          }
          if (Number(value) < 1) {
            return "Patient goal must be at least 1";
          }
          return true;
        },
      },
      width: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
      tooltip: "Enter the target number of patients expected to be served during this event",
    },
    {
      type: "input",
      name: "poc_name",
      label: "Point of Contact Name",
      placeholder: "Enter point of contact name",
      fieldtype: "text",
      width: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
      tooltip: "Enter the name of the point of contact person for this event",
    },
    {
      type: "input",
      name: "poc_phone_number",
      label: "Point of Contact Phone Number",
      placeholder: "Enter point of contact phone number",
      format_type: "phone",
      inputRules: {
        pattern: {
          value: /^(\(\d{3}\) \d{3}-\d{4}|\d{3}-\d{3}-\d{4})$/,
          message: "Phone number must be exactly 10 digits",
        },
      },
      width: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
      tooltip: "Enter the phone number of the point of contact person for this event",
    },
    ...(isCompletedEvent ? patientVisitSummaryFields : []),
    {
      type: "textbox",
      name: "description",
      label: replaceTermInText(`${itemTitle} Description`),
      placeholder: "",
      inputRules: {
        // required: "This field is required",
      },
      width: "w-full",
      for: ["admin"],
      tooltip: replaceTermInText(
        `Provide a brief overview of the ${itemName}'s purpose and content`
      ),
    },
    {
      type: "input",
      name: "address_line1",
      label: "Address Line 1",
      placeholder: "Enter Address Line 1",
      autoComplete: "address_line1",
      inputRules: {
        // required: "This field is required",
        maxLength: 255,
      },
      width: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
      for: ["admin", "mentor"],
    },
    {
      type: "input",
      name: "address_line2",
      label: "Address Line 2",
      autoComplete: "address_line2",
      placeholder: "Enter Address Line 2",
      inputRules: {
        maxLength: 255,
        // required: 'This field is required',
      },
      width: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
      for: ["admin", "mentor"],
    },
    {
      type: "search",
      name: "city",
      label: "City",
      placeholder: "Enter City",
      inputRules: {
        // required: "This field is required",
      },
      options: [],
      autoComplete: "city",
      searchParams: LOCATION_SEARCH_PARAMS,
      width: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
      for: ["admin", "mentor"],
    },
    {
      type: "search",
      name: "county",
      label: "County",
      placeholder: "Enter County",
      options: [],
      autoComplete: "county",
      searchParams: LOCATION_SEARCH_PARAMS,
      width: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
      for: ["admin", "mentor"],
    },
    {
      type: "search",
      name: "state_name",
      label: "State",
      placeholder: "Enter State",
      inputRules: {
        // required: "This field is required",
      },
      options: [],
      autoComplete: "state_name-off",
      defaultValue: DEFAULT_STATE,
      isDisable: true,
      width: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
      for: ["admin", "mentor"],
    },
    {
      type: "search",
      name: "zip_code",
      label: "Zip code",
      placeholder: "Enter Zip code",
      inputRules: {
        // required: "This field is required",
        pattern: {
          value: /^\d{5}(-\d{4})?$/,
          message: "ZIP code must be in the format XXXXX or XXXXX-XXXX.",
        },
      },
      autoComplete: "zip_code",
      searchParams: LOCATION_SEARCH_PARAMS,
      width: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
      for: ["admin", "mentor"],
    },
    {
      type: "popup-input",
      name: "equipments",
      label: "Required Mobile Unit",
      fieldtype: "text",
      placeholder: "Add required Mobile Unit",
      inputRules: {
        // required: 'This field is required',
      },
      width: "w-full",
      icon: "add",
      for: ["mentor"],
    },
    {
      type: "boolean-checkbox",
      name: "provider_needed",
      label: "Provider needed",
      clears: "assign_providers",
      width: "w-full",
      for: ["admin"],
    },
    {
      type: "popup-input",
      name: "assign_providers",
      label: "Assign Provider",
      placeholder: "Assign Provider",
      fieldtype: "text",
      showWhen: "provider_needed",
      inputRules: {
        // required: "This field is required",
      },
      width: "w-full lg:w-full md:w-full xl:w-full",
      icon: "add",
      for: ["admin"],
      tooltip: `Select the provider assigned to this ${programType}.`,
    },
    {
      type: "boolean-checkbox",
      name: "ma_needed",
      label: "Medical Assistant needed",
      clears: "assign_medical_assistants",
      width: "w-full",
      for: ["admin"],
    },
    {
      type: "popup-input",
      name: "assign_medical_assistants",
      label: "Assign Medical Assistant",
      placeholder: "Assign Medical Assistant",
      fieldtype: "text",
      showWhen: "ma_needed",
      inputRules: {
        // required: "This field is required",
      },
      width: "w-full lg:w-full md:w-full xl:w-full",
      icon: "add",
      for: ["admin"],
      tooltip: `Select a medical assistant to support this ${programType}.`,
    },
    {
      type: "boolean-checkbox",
      name: "hta_needed",
      label: "HTA needed",
      clears: "assign_hta",
      width: "w-full",
      for: ["admin"],
    },
    {
      type: "popup-input",
      name: "assign_hta",
      label: "Assign Healthcare Transportation Associate",
      placeholder: "Assign Healthcare Transportation Associate",
      fieldtype: "text",
      showWhen: "hta_needed",
      width: "w-full lg:w-full md:w-full xl:w-full",
      icon: "add",
      for: ["admin"],
      tooltip: `Select a transportation associate to coordinate transportation for this ${programType}.`,
    },
    {
      type: "support-staff",
      name: "support_staff_needed",
      label: "Support staff needed",
      width: "w-full",
      newRow: true,
      for: ["admin"],
    },
    {
      type: "popup-input",
      name: "assign_supervisors",
      label: "Assign Event Supervisor",
      placeholder: "Assign Event Supervisor",
      fieldtype: "text",
      width: "w-full lg:w-full md:w-full xl:w-full",
      icon: "add",
      for: ["admin"],
      tooltip: `Select the supervisor assigned to this event.`,
    },
    ...(isCompletedEvent
      ? [
          {
            type: "input",
            name: "set_up_time",
            label: "Setup Time",
            placeholder: "MM/DD/YYYY HH:MM",
            fieldtype: "text",
            width: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
            disabled: true,
            tooltip: "Automatically calculated as the time elapsed between the event start time and the HCTA check-in time",

          },
          {
            type: "input",
            name: "take_down_time",
            label: "Takedown Time",
            placeholder: "MM/DD/YYYY HH:MM",
            fieldtype: "text",
            width: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
            disabled: true,
            tooltip: "Automatically calculated as the time elapsed between the event end time and the HCTA check-out time",
          },
        ]
      : []),
    {
      type: "textbox",
      name: "special_instructions",
      label: "Special Instructions",
      placeholder: "Enter any special instructions for this event",
      width: "w-full",
      tooltip: "Provide event-specific instructions or important details",
    },
    {
      type: "file",
      name: "program_image",
      label: replaceTermInText(`${itemTitle} Image`),
      fieldtype: "text",
      placeholder: "Multiple Skills added",
      inputRules: {
        // required: 'This field is required',
      },
      width: "w-full",
      for: ["mentor"],
      is_image: true,
      tooltip: replaceTermInText(
        `Upload an image that represents the ${itemName} visually`
      ),
    },
    // {
    //   type: "radio",
    //   name: "recurring_program",
    //   label: replaceTermInText(
    //     `Do you want this ${
    //       is_event ? "event" : `${itemName}`
    //     } to be recurring?`
    //   ),
    //   placeholder: "",
    //   options: [
    //     { key: "true", value: "Yes" },
    //     { key: "false", value: "No" },
    //   ],
    //   inputRules: {
    //     // required: "This field is required",
    //   },
    //   width: "w-full lg:w-full md:w-[48%] xl:w-full",
    //   for: ["mentor"],
    //   tooltip: replaceTermInText(
    //     `Choose if the ${
    //       is_event ? "event" : `${itemName}`
    //     } as a recurring frequency (e.g., one-time, weekly, monthly)`
    //   ),
    // },
    // {
    //   type: "dynamicFields",
    //   name: "recurring_dates",
    //   label: "",
    //   for: ["mentor"],
    //   width: "w-full",
    //   dynamicFields: [
    //     {
    //       type: "mui_date",
    //       name: "start_date",
    //       label: "Start Date and Time",
    //       placeholder: "Select Start Date and Time",
    //       inputRules: {
    //         required: "Start Date and Time is required",
    //       },
    //       width: "w-full lg:w-[49%] md:w-[48%] xl:w-[49%]",
    //       for: ["mentor"],
    //     },
    //     {
    //       type: "mui_date",
    //       name: "end_date",
    //       label: "End Date and Time",
    //       placeholder: "Select End Date and Time",
    //       inputRules: {
    //         required: "End Date and Time is required",
    //       },
    //       width: "w-full lg:w-[49%] md:w-[48%] xl:w-[49%]",
    //       for: ["mentor"],
    //     },
    //     {
    //       type: "program_frequency",
    //       name: "reminder_type",
    //       label: "Frequency",
    //       placeholder: "",
    //       options: [
    //         {
    //           key: "daily",
    //           value: "Daily",
    //         },
    //         {
    //           key: "weekly_byday",
    //           value: "Weekly",
    //         },
    //         {
    //           key: "month_week",
    //           value: "Monthly",
    //         },
    //       ],
    //       inputRules: {
    //         // required: "This field is required",
    //       },
    //       size: true,
    //       width: "w-[49%]",
    //       for: ["mentor"],
    //       tooltip: replaceTermInText(
    //         `Select how often sessions are held during the ${itemName}`
    //       ),
    //     },
    //   ],
    // },

    // {
    //   type: "dropdown",
    //   name: "certificate_type",
    //   label: "Certificate Type",
    //   placeholder: "Select Certificate Type",
    //   tooltip: "Choose the type of certificate participants will receive",
    //   inputRules: {
    //     // required: "This field is required",
    //   },
    //   options: [
    //     {
    //       key: "bronze",
    //       value: "Bronze",
    //     },
    //     {
    //       key: "silver",
    //       value: "Silver",
    //     },
    //     {
    //       key: "gold",
    //       value: "Gold",
    //     },
    //   ],
    //   width: "w-full lg:w-[49%] md:w-[48%] xl:w-[49%]",
    //   for: ["admin"],
    // },

    // {
    //   type: "new_file",
    //   name: "program_certificate",
    //   label: "Upload Certificates",
    //   fieldtype: "text",
    //   // placeholder: "Add Certificates",
    //   inputRules: {
    //     // required: "This field is required",
    //   },
    //   width: "w-full lg:w-[49%] md:w-[48%] xl:w-[49%]",
    //   icon: "add",
    //   for: ["admin", "mentor"],
    //   tooltip: "Upload certificate templates or designs",
    // },

    // {
    //   type: "radio",
    //   name: "is_sponsored",
    //   label: "Sponsor:",
    //   placeholder: "",
    //   options: [
    //     { key: "true", value: "Yes" },
    //     { key: "false", value: "No" },
    //   ],
    //   inputRules: {
    //     // required: "This field is required",
    //   },
    //   size: true,
    //   for: ["admin", "mentor"],
    //   tooltip: replaceTermInText(
    //     `Add sponsor details for the ${itemName} if any`
    //   ),
    // },

    // {
    //   type: "input",
    //   name: "sponsor_name",
    //   label: "Name of the Sponsor",
    //   fieldtype: "text",
    //   placeholder: "Enter name of the Sponsor",
    //   inputRules: {
    //     // required: "This field is required",
    //   },
    //   width: "w-[100%] sm:w-[100%] md:w-[48%] lg:w-[48%] xl:w-[48%]",
    //   for: ["admin", "mentor"],
    //   tooltip: replaceTermInText(
    //     `Enter the sponsor's name associated with this ${itemName}`
    //   ),
    // },
    // {
    //   type: "input",
    //   name: "enrollment_fees",
    //   label: "Fees",
    //   fieldtype: "number",
    //   placeholder: "Enter fees",
    //   inputRules: {
    //     required: "This field is required",
    //   },
    //   width: "w-[80%]",
    //   for: ["admin", "mentor"],
    // },
    // {
    //   type: "file",
    //   name: "sponsor_logos",
    //   label: "Add Sponsor Logo",
    //   fieldtype: "text",
    //   placeholder: "Multiple Skills added",
    //   inputRules: {
    //     // required: "This field is required",
    //   },
    //   width: "w-full",
    //   for: ["admin", "mentor"],
    // },
  ];

  // if (
  //   selectedTheme === tenants.VMS ||
  //   selectedTheme === tenants.TIM ||
  //   selectedTheme === tenants.BPS
  // ) {
  //   programInformationOptions.splice(2, 0, typeOfProgramField);
  // }
  return programInformationOptions;
};

export const AboutProgramFields = [
  // {
  //   type: "popup-input",
  //   name: "certifications",
  //   label: "Program Certificates",
  //   fieldtype: "text",
  //   placeholder: "Add Certificates",
  //   inputRules: {
  //     // required: "This field is required",
  //   },
  //   width: "w-full",
  //   icon: "add",
  //   for: ["admin", "mentor"],
  // },

  {
    type: "radio",
    name: "is_sponsored",
    label: "Sponsor:",
    placeholder: "",
    options: [
      { key: "true", value: "Yes" },
      { key: "false", value: "No" },
    ],
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    for: ["admin", "mentor"],
  },
  {
    type: "input",
    name: "enrollment_fees",
    label: "Fees",
    fieldtype: "number",
    placeholder: "Enter fees",
    inputRules: {
      // required: "This field is required",
    },
    width: "w-[80%]",
    for: ["admin", "mentor"],
  },
  {
    type: "file",
    name: "sponsor_logos",
    label: "Add Sponsor Logo",
    fieldtype: "text",
    placeholder: "Multiple Skills added",
    inputRules: {
      // required: "This field is required",
    },
    width: "w-full",
    for: ["admin", "mentor"],
  },
];

export const ProgramFields = [
  getProgramInformationFields(),
  AboutProgramFields,
];

export const AssignMenteesFields = (
  bool = true,
  type,
  isFieldSession = false
) => {
  return [
    {
      type: "input",
      name: "program_id",
      fieldtype: "text",
      label: `${itemTitle} Name`,
      placeholder: `Enter ${itemTitle} Name`,
      inputRules: {
        required: "This field is required",
      },
      width: "w-full lg:w-[49%] md:w-[49%] xl:w-[49%]",
      disabled: type === "new" ? false : true,
      autoComplete: "off", // Disable browser autocomplete
      options: [],
      tooltip: `Select the ${itemName} this assignment belongs to`,
    },

    {
      type: "dropdown",
      name: "goal_id",
      fieldtype: "text",
      label: "Sub Event Name",
      placeholder: "Select Sub Events",
      width: "w-full lg:w-[49%] md:w-[49%] xl:w-[49%]",
      // inputRules: {
      //   required: "This field is required",
      // },

      // disabled: bool,
      options: [],
      tooltip: "Choose a sub event the assignment is linked to",
    },
    {
      type: "input",
      name: "task_name",
      fieldtype: "text",
      label: "Assignment Name",
      placeholder: "Enter Assignment Name",
      inputRules: {
        required: "This field is required",
        maxLength: 250,
      },

      width: "w-full lg:w-[32.33%] md:w-[32.33%] xl:w-[32.33%]",
      disabled: false,
      options: [],
      tooltip: "Enter a title or name for the assignment",
    },

    {
      type: "date",
      name: "start_date",
      label: "Assignment Start Date",
      placeholder: "Select Assignment Start Date ",
      inputRules: {
        required: "This field is required",
      },
      width: "w-full lg:w-[32.33%] md:w-[32.33%] xl:w-[32.33%]",
      tooltip: `Set when the assignment becomes available to ${
        isFieldSession ? "Volunteers" : MenteesType
      }`,
    },
    {
      type: "date",
      name: "end_date",
      label: "Assignment End Date",
      placeholder: "Select Assignment End Date",
      inputRules: {
        required: "This field is required",
      },
      width: "w-full lg:w-[32.33%] md:w-[32.33%] xl:w-[32.33%]",
      tooltip: "Set the deadline by which the assignment should be completed",
    },
    {
      type: "time",
      name: "start_time",
      label: "Assignment Start Time",
      placeholder: "Select Assignment Start Time",
      inputRules: {
        required: "This field is required",
      },
      width: "w-full lg:w-[49%] md:w-[49%] xl:w-[49%]",
      tooltip: `Set when the assignment becomes available to ${
        isFieldSession ? "Volunteers" : MenteesType
      }`,
    },
    {
      type: "time",
      name: "end_time",
      label: "Assignment End Time",
      placeholder: "Select Assignment End Time",
      inputRules: {
        required: "This field is required",
      },
      width: "w-full lg:w-[49%] md:w-[49%] xl:w-[49%]",
      tooltip: "Set the deadline by which the assignment should be completed",
    },
    {
      type: "editor",
      name: "task_description",
      fieldtype: "text",
      label: "Assignment Description",
      placeholder: "Enter Assignment Description",
      // inputRules: {
      //   required: "This field is required",
      // },
      width: "w-full",
      disabled: false,
      options: [],
      tooltip: "Provide detailed instructions or context for the assignment",
    },

    {
      type: "text",
      name: `mentees_list`,

      label: `Add ${
        isFieldSession ? "volunteers" : MenteesType.toLowerCase()
      } for this Assignment`,
      width: "w-full",
      inputRules: {
        required: "This field is required",
      },
      tooltip: `Select the ${
        isFieldSession ? "volunteers" : MenteesType
      } who should work on the assignment`,
    },

    {
      type: "input",
      name: "reference_links",
      fieldtype: "text",
      label: "Reference",
      placeholder: "Enter Reference with comma(,) seperators",
      // inputRules: {
      //   required: "This field is required",
      // },
      width: "w-full",
      tooltip: "Include any links related to the assignment",
    },
    {
      name: "task_documents",
      label: "Reference Materials",
      type: "upload",
      width: "w-full",
      isMultiFile: true,
      tooltip: "Include any supporting material related to the assignment",
    },
    // {
    //   type: "dropdown",
    //   name: "category_id",
    //   label: "Category",
    //   placeholder: "Select Category",
    //   inputRules: {
    //     required: "This field is required",
    //   },
    //   options: [
    //     {
    //       key: "yes",
    //       value: "Category 1",
    //     },
    //     {
    //       key: "no",
    //       value: "Category 2",
    //     },
    //   ],
    //   width: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
    //   disabled: bool,
    // },
    // {
    //   type: "input",
    //   name: "mentor",
    //   fieldtype: "text",
    //   label: "Mentor Name",
    //   placeholder: "Enter Mentor Name",
    //   inputRules: {
    //     // required: "This field is required",
    //   },
    //   width: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
    //   disabled: true,
    //   options: [],
    // },
    // {
    //   type: "input",
    //   name: "duration",
    //   label: "Program Duration",
    //   fieldtype: "text",
    //   placeholder: "Program Duration",
    //   inputRules: {
    //     required: "This field is required",
    //   },
    //   width: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
    //   disabled: true,
    // },
    // {
    //   type: "input",
    //   name: "task_name",
    //   fieldtype: "text",
    //   label: "Interaction Point/Assignment Name",
    //   placeholder: "Enter Interaction Point/Assignment",
    //   inputRules: {
    //     required: "This field is required",
    //   },
    //   width: "w-full",
    // },
    // {
    //   type: "editor",
    //   name: "task_details",
    //   fieldtype: "text",
    //   label: "Interaction Point/Assignment Details",
    //   placeholder: "List out Interaction Point/Assignment Details",
    //   inputRules: {
    //     required: "This field is required",
    //   },
    //   width: "w-full",
    // },
    // {
    //   type: "date",
    //   name: "due_date",
    //   label: "Due Date",
    //   placeholder: "Due Date",
    //   inputRules: {
    //     required: "This field is required",
    //   },
    //   width: "w-full",
    // },
  ];
};

export const ReportFields = (bool = false) => [
  {
    type: "dropdown",
    name: "category",
    label: "Category",
    placeholder: "Select Category",
    inputRules: {
      required: "This field is required",
    },
    options: [],
    width: "w-full lg:w-[48%] md:w-[48%] xl:w-[49%]",
    disabled: bool,
    tooltip: "Choose the category the insight belongs to",
  },
  {
    type: "dropdown",
    name: "program",
    label: `${itemTitle} Name`,
    placeholder: `Select ${itemTitle} Name`,
    inputRules: {
      required: "This field is required",
    },
    options: [],
    width: "w-full lg:w-[48%] md:w-[48%] xl:w-[49%]",
    disabled: bool,
    tooltip: `Select the ${itemTitle} this insight is linked to`,
  },

  // {
  //   type: "dropdown",
  //   name: "mentor_manager",
  //   label: "Program Creator",
  //   placeholder: "Select Mentor Manager",
  //   inputRules: {
  //     // required: "This field is required",
  //   },
  //   options: [],
  //   width: 'width-32',
  // },
  // {
  //   type: "input",
  //   name: "mentor_name",
  //   fieldtype: "text",
  //   label: "Mentor Name",
  //   placeholder: "Mentor Name",
  //   inputRules: {
  //     // required: "This field is required",
  //   },
  //   width: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
  //   disabled: true,
  // },
  // {
  //   type: "date",
  //   name: "start_date",
  //   label: "Program Start Date and Time",
  //   placeholder: "Select Program Start Date and Time",
  //   inputRules: {
  //     required: "This field is required",
  //   },
  //   width: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
  //   disabled: bool,
  // },
  // {
  //   type: "date",
  //   name: "end_date",
  //   label: "Program End Date and Time",
  //   placeholder: "Select Program End Date and Time",
  //   inputRules: {
  //     required: "This field is required",
  //   },
  //   width: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
  //   disabled: bool,
  // },
  // {
  //   type: "popup-input",
  //   name: "participated_mentees",
  //   label: "Participated ${MenteesType}",
  //   placeholder: "Select Meneteed",
  //   inputRules: {
  //     // required: "This field is required",
  //   },
  //   options: [],
  //   width: "w-full",
  //   // disabled: false,
  // },
  {
    type: "input",
    name: "report_name",
    fieldtype: "text",
    label: "Insight Name",
    placeholder: "Enter Insight Name",
    inputRules: {
      required: "This field is required",
      maxLength: 255,
    },
    width: "w-full",
    tooltip: "Enter a title for the insight",
  },
  {
    type: "textarea",
    name: "description",
    label: "Description",
    placeholder: "Enter Description",
    inputRules: {
      // required: "This field is required",
    },
    width: "w-full",
    tooltip: "Provide additional context or explanation for the insight",
  },
  // {
  //   type: "editor",
  //   name: "description",
  //   fieldtype: "text",
  //   label: "Insight Link",
  //   placeholder: "Insight Link",
  //   // inputRules: {
  //   //   required: 'This field is required',
  //   // },
  //   width: "w-full",
  //   disabled: bool,
  // },
];

export const EditProfileFields = [
  {
    type: "input",
    name: "name",
    fieldtype: "text",
    label: "Name",
    placeholder: "Enter Name",
    inputRules: {
      // required: "This field is required",
    },
    width: "width-49",
    disabled: true,
  },
  {
    type: "input",
    name: "position",
    fieldtype: "text",
    label: "Postion",
    placeholder: "Enter Postion",
    inputRules: {
      // required: "This field is required",
    },
    width: "width-49",
  },
  {
    type: "textbox",
    name: "professional_bio",
    label: "Professional Bio",
    placeholder: "Enter Professional Bio",
    inputRules: {
      required: "This field is required",
    },
    width: "w-full",
    disabled: true,
  },
  {
    type: "input",
    name: "link",
    fieldtype: "text",
    label: "Link",
    placeholder: "Enter Link",
    inputRules: {
      required: "This field is required",
    },
    width: "width-49",
  },
  {
    type: "input",
    name: "email",
    fieldtype: "text",
    label: "Email",
    placeholder: "Enter Email",
    inputRules: {
      required: "This field is required",
    },
    width: "width-49",
    disabled: true,
  },
  {
    type: "input",
    name: "phone_number",
    // fieldtype: 'number',
    label: "Phone Number",
    placeholder: "Enter Phone Number",
    inputRules: {
      required: "This field is required",
      pattern: {
        value: /^(\(\d{3}\) \d{3}-\d{4}|\d{3}-\d{3}-\d{4})$/,
        message: "Invalid phone number format",
      },
    },
    width: "width-49",
  },
  {
    type: "input",
    name: "location",
    fieldtype: "text",
    label: "Location",
    placeholder: "Enter Location",
    inputRules: {
      required: "This field is required",
    },
    width: "width-49",
    icon: "location",
  },
  {
    type: "input",
    name: "address",
    fieldtype: "text",
    label: "Address",
    placeholder: "Enter Address",
    inputRules: {
      required: "This field is required",
    },
    width: "w-full",
  },
  {
    type: "input",
    name: "social_media",
    fieldtype: "text",
    label: "Social Media",
    placeholder: "Social Media",
    inputRules: {
      required: "This field is required",
    },
    width: "w-full",
  },
  // {
  //   type: "logo",
  //   name: "logo",
  //   fieldtype: "text",
  //   label: "Social Media",
  //   placeholder: "Enter Address",
  //   inputRules: {
  //     required: "This field is required",
  //   },
  //   width: 'w-full',
  // }
];

export const reportColumns = (clickHandler) => [
  {
    field: "name",
    headerName: "Insights Name",
    flex: 1,
    minWidth: 150,
    id: 3,
    status: ["all", "new", "pending", "approved", "rejected", "draft"],
    renderCell: (params) => {
      return (
        <span
          className="text-font-primary-main underline max-w-3 truncate cursor-pointer"
          onClick={() => clickHandler(params?.row)}
        >
          {params?.row?.name}
        </span>
      );
    },
  },
  {
    field: "category_name",
    headerName: "Category",
    minWidth: 150,
    flex: 1,
    id: 0,
    status: ["all", "new", "pending", "approved", "rejected", "draft"],
  },
  {
    field: "program_name",
    headerName: `${itemTitle} Name`,
    minWidth: 150,
    flex: 1,
    id: 1,
    status: ["all", "new", "pending", "approved", "rejected", "draft"],
  },
  {
    field: "participates_count",
    headerName: `${MenteesType}`,
    headerAlign: "center",
    align: "center",
    cellClassName: "center-aligned-cell",
    minWidth: 150,
    flex: 1,
    id: 2,
    status: ["all", "new", "pending", "approved", "rejected", "draft"],
  },
  // {
  //   field: 'created_at',
  //   headerName: 'Create Time/ Date',
  //   flex: 1,
  //   id: 4,
  //   status: ['all', 'new', 'pending', 'approved', 'rejected']
  // },
  {
    field: "created_at",
    headerName: "Requested Date",
    minWidth: 150,
    flex: 1,
    id: 4,
    status: ["all", "new", "pending", "approved", "rejected"],
    renderCell: (params) => <span>{dateFormat(params?.row?.created_at)}</span>,
  },
  {
    field: "approved_date",
    headerName: "Approved Date",
    minWidth: 150,
    flex: 1,
    id: 5,
    status: ["all", "new", "pending", "approved", "rejected"],
    renderCell: (params) => {
      const dateValue = params.row.approved_date;

      // Show ". . ." if the value is null, undefined, or empty
      if (!dateValue || dateValue === ". . .") {
        return <span>. . .</span>;
      }

      return <span>{dateFormat(dateValue)}</span>;
    },
  },
  {
    field: "approved_by_full_name",
    headerName: "Approved by",
    minWidth: 150,
    flex: 1,
    id: 6,
    status: ["all", "new", "pending", "approved", "rejected"],
  },
  // {
  //   field: "updated_at",
  //   headerName: "Last updated Date",
  //   flex: 1,
  //   id: 5,
  //   status: ["all", "new", "pending", "approved", "rejected"],
  //   renderCell: (params) => <span>{dateFormat(params.row.updated_at)}</span>,
  // },
  // {
  //   field: "last_updated_by",
  //   headerName: "Last updated by",
  //   flex: 1,
  //   id: 6,
  //   status: ["all", "new", "pending", "approved", "rejected"],
  // },
  // {
  //   field: 'rejected_date',
  //   headerName: 'Rejected Date',
  //   flex: 1,
  //   id: 7,
  //   status: ['all', 'cancel'],
  //   renderCell: (params) => <span>{dateFormat(params.row.rejected_date)}</span>
  // },
  // {
  //   field: 'rejecter',
  //   headerName: 'Rejected by',
  //   flex: 1,
  //   id: 8,
  //   status: ['all', 'cancel']
  // },
  // {
  //   field: 'file',
  //   headerName: 'File',
  //   flex: 1,
  //   id: 9,
  //   status: ['all', 'new', 'pending', 'approved', 'cancel', 'draft']
  // },
];

export const myMentorColumns = [
  // {
  //   field: "job_title",
  //   headerName: "Designation",
  //   flex: 1,
  //   id: 1,
  // },
  // {
  //   field: "areas_of_expertise",
  //   headerName: "Skills",
  //   flex: 1,
  //   id: 2,
  //   renderCell: (params) => {
  //     let areasOfExpertise = "...";
  //     if (!!params?.row?.areas_of_expertise?.length) {
  //       areasOfExpertise = params?.row?.areas_of_expertise?.toString();
  //     }
  //     return <span>{areasOfExpertise}</span>;
  //   },
  // },
  // {
  //   field: "job_title",
  //   headerName: "Designation",
  //   flex: 1,
  //   id: 1,
  // },
  // {
  //   field: "areas_of_expertise",
  //   headerName: "Skills",
  //   flex: 1,
  //   id: 2,
  //   renderCell: (params) => {
  //     let areasOfExpertise = '...';
  //     if (!!params?.row?.areas_of_expertise?.length) {
  //       areasOfExpertise = params?.row?.areas_of_expertise?.toString();
  //     }
  //     return <span>{areasOfExpertise}</span>
  //   }

  // },
  {
    field: "email",
    headerName: "Email",
    flex: 1,
    // headerClassName: "bg-background-primary-secondary",
    minWidth: 150,
    id: 3,
  },
  {
    field: "location",
    headerName: "Location",
    // headerClassName: "bg-background-primary-secondary",
    flex: 1,
    minWidth: 150,
    id: 4,
    sortable: false,
    // align: "center",
    // headerAlign: "center",
    renderCell: (params) => renderLocationCell(params?.row?.location_data),
  },
];

export const CreateCertificateFields = [
  {
    type: "dropdown",
    name: "category",
    label: "Category",
    placeholder: "Select Category",
    inputRules: {
      required: "This field is required",
    },
    isRequired: true,
    options: [],
    width: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
  },
  {
    type: "dropdown",
    name: "program",
    label: `${itemTitle} Name`,
    placeholder: `Select ${itemTitle} Name`,
    inputRules: {
      required: "This field is required",
    },
    options: [],
    isRequired: true,
    width: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
  },
  {
    type: "input",
    name: "mentor_name",
    fieldtype: "text",
    label: `${MentorType} Name`,
    placeholder: `${MentorType} Name`,
    inputRules: {},
    width: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
    disabled: true,
  },
  {
    type: "input",
    name: "course_level",
    fieldtype: "text",
    label: "Course Level",
    placeholder: "Course Level",
    inputRules: {},
    width: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
    disabled: true,
  },
  {
    type: "date",
    name: "start_date",
    label: `${itemTitle} Start Date and Time`,
    placeholder: `Select ${itemTitle} Start Date and Time`,
    inputRules: {
      required: "This field is required",
    },

    width: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
    disabled: true,
  },
  {
    type: "date",
    name: "end_date",
    label: `${itemTitle} End Date and Time`,
    placeholder: `Select ${itemTitle} End Date and Time`,
    inputRules: {
      required: "This field is required",
    },
    width: "w-full lg:w-[32%] md:w-[48%] xl:w-[32%]",
    disabled: true,
  },
  {
    type: "input",
    name: "duration",
    label: "Duration",
    placeholder: "Duration",
    inputRules: {},
    width: "w-full lg:w-[49%] md:w-[48%]",
    disabled: true,
  },
  {
    type: "popup-input",
    name: `participated_mentees`,
    label: `Participated ${MenteesType}`,
    placeholder: `${isVMSTheme ? "Select Volunteered" : "Select Meneteed"}`,
    inputRules: {},
    options: [],
    width: "w-full lg:w-[49%] md:w-[48%]",
  },
  {
    type: "popup-input",
    name: `pass_mentee_list`,

    label: `Pass ${MenteesType}`,
    placeholder: `${isVMSTheme ? "Select Volunteered" : "Select Meneteed"}`,

    inputRules: {},
    options: [],
    width: "w-full lg:w-[49%] md:w-[48%]",
  },
  {
    type: "popup-input",
    name: `fail_mentee_list`,

    label: `No Pass ${MenteesType}`,
    placeholder: `${isVMSTheme ? "Select Volunteered" : "Select Meneteed"}`,
    inputRules: {},
    options: [],
    width: "w-full lg:w-[49%] md:w-[48%]",
  },
];

export const ProfileFields = [
  {
    type: "input",
    name: "first_name",
    fieldtype: "text",
    label: "First Name",
    placeholder: "Enter First Name",
    inputRules: {},
    disabled: true,
    width: "col-span-2",
  },
  {
    type: "input",
    name: "last_name",
    fieldtype: "text",
    label: "Last Name",
    placeholder: "Enter Last Name",
    inputRules: {},
    disabled: true,
    width: "col-span-2",
  },
  {
    type: "input",
    name: "phone_number",
    fieldtype: "number",
    label: "Primary Contact Number",
    placeholder: "Enter Primary Contact Number",
    inputRules: {
      required: "This field is required",
      pattern: {
        value: /^(\(\d{3}\) \d{3}-\d{4}|\d{3}-\d{3}-\d{4})$/,
        message: "Invalid phone number format",
      },
    },
    width: "col-span-2",
  },
  {
    type: "input",
    name: "secondary_phone_number",
    fieldtype: "number",
    label: "Secondary Contact Number",
    placeholder: "Enter Secondary Contact Number",
    inputRules: {
      pattern: {
        value: /^(\(\d{3}\) \d{3}-\d{4}|\d{3}-\d{3}-\d{4})$/,
        message: "Invalid phone number format",
      },
    },
    width: "col-span-2",
  },
  {
    type: "input",
    name: "email",
    fieldtype: "text",
    label: "Email",
    placeholder: "Enter Email",
    inputRules: {},
    disabled: true,
    width: "col-span-2",
  },
  {
    type: "input",
    name: "address",
    fieldtype: "text",
    label: "Address",
    placeholder: "Enter Address",
    inputRules: {},
    width: "col-span-2",
  },
  {
    type: "input",
    name: "strongest_skills",
    fieldtype: "text",
    label: "Skills",
    placeholder: "Enter skills",
    inputRules: {},
    width: "col-span-2",
  },
  // {
  //   type: 'input',
  //   name: 'category',
  //   fieldtype: 'text',
  //   label: 'Category',
  //   placeholder: 'Enter category',
  //   inputRules: {},
  //   width: 'col-span-2',
  // },
  {
    type: "input",
    name: "Professional_Bio",
    fieldtype: "text",
    label: "Professional Bio",
    placeholder: "Enter category",
    inputRules: {},
    width: "col-span-6",
  },
  // {
  //   type: 'input',
  //   name: 'documents',
  //   fieldtype: 'text',
  //   label: 'Documents',
  //   placeholder: 'Enter category',
  //   inputRules: {},
  //   width: 'col-span-6',
  // },
];

export const categoryCreateEditFields = [
  {
    type: "input",
    name: "category_name",
    fieldtype: "text",
    label: "Specialty Name",
    placeholder: "Enter Specialty Name",
    inputRules: {
      required: "This field is required",
    },
    width: "w-full",
    for: ["admin"],
    tooltip: "Create a unique specialty name",
  },
  {
    type: "textbox",
    name: "description",
    label: "Description",
    placeholder: "Enter Description",
    inputRules: {
      // required: "This field is required",
    },
    width: "w-full",
    for: ["admin"],
    tooltip: "Add details to help users understand this specialty's purpose",
  },
];

export const PersonalInfoFields = [
  {
    type: "input",
    name: "first_name",
    fieldtype: "text",
    label: "First Name",
    placeholder: "Enter First Name",
    inputRules: {
      required: "This field is required",
    },
    size: true,
    disabled: true,
    width: "col-span-6 sm:col-span-6 md:col-span-3 lg:col-span-2 xl:col-span-2",
  },
  {
    type: "input",
    name: "last_name",
    fieldtype: "text",
    label: "Last Name",
    placeholder: "Enter Last Name",
    inputRules: {
      required: "This field is required",
    },
    size: true,
    disabled: true,
    width: "col-span-6 sm:col-span-6 md:col-span-3 lg:col-span-2 xl:col-span-2",
  },
  {
    type: "input",
    name: "email",
    fieldtype: "email",
    label: "E-mail",
    placeholder: "Enter Email",
    inputRules: {
      required: "This field is required",
    },
    size: true,
    disabled: true,
    width: "col-span-6 sm:col-span-6 md:col-span-3 lg:col-span-2 xl:col-span-2",
  },
  {
    type: "input",
    name: "phone_number",
    // fieldtype: 'number',
    label: "Primary Contact Number",
    placeholder: "Enter Primary Contact Number",
    inputRules: {
      required: "This field is required",
      pattern: {
        value: /^(\(\d{3}\) \d{3}-\d{4}|\d{3}-\d{3}-\d{4})$/,
        message: "Invalid phone number format",
      },
    },
    size: true,
    width: "col-span-6 sm:col-span-6 md:col-span-3 lg:col-span-2 xl:col-span-2",
  },
  {
    type: "input",
    name: "secondary_phone_number",
    // fieldtype: 'number',
    label: "Secondary Contact Number",
    placeholder: "Enter Secondary Contact Number",
    inputRules: {
      pattern: {
        value: /^(\(\d{3}\) \d{3}-\d{4}|\d{3}-\d{3}-\d{4})$/,
        message: "Invalid phone number format",
      },
    },
    size: true,
    width: "col-span-6 sm:col-span-6 md:col-span-3 lg:col-span-2 xl:col-span-2",
  },
  // {
  //   type: 'input',
  //   name: 'phone_number',
  //   fieldtype: 'number',
  //   label: 'Primary Contact Number',
  //   placeholder: 'Enter Primary Contact Number',
  //   inputRules: {
  //     required: 'This field is required',
  //     pattern: {
  //       value: /^(\(\d{3}\) \d{3}-\d{4}|\d{3}-\d{3}-\d{4})$/,
  //       message: 'Invalid phone number format',
  //     },
  //   },
  //   size: true,
  //   width: 'col-span-2',
  // },
  // {
  //   type: "input",
  //   name: "secondary_phone_number",
  //   fieldtype: "number",
  //   label: "Secondary Contact Number",
  //   placeholder: "Enter Secondary Number",
  //   inputRules: {
  //     // required: "This field is required",
  //     pattern: {
  //       value: /^(\(\d{3}\) \d{3}-\d{4}|\d{3}-\d{3}-\d{4})$/,
  //       message: "Invalid phone number format",
  //     },
  //   },
  //   size: true,
  //   width: "col-span-2",
  // },
  {
    type: "input",
    name: "address",
    label: "Address",
    placeholder: "Enter you address",
    inputRules: {
      // required: 'This field is required',
    },
    size: true,
    width: "col-span-6 sm:col-span-6 md:col-span-3 lg:col-span-2 xl:col-span-2",
  },
  {
    type: "input",
    name: "linked_in",
    fieldtype: "text",
    label: "LinkedIn Profile Link",
    placeholder: "Enter LinkedIn Profile Link",
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    width: "col-span-6 sm:col-span-6 md:col-span-3 lg:col-span-2 xl:col-span-2",
  },
  {
    type: "radio",
    name: "gender",
    label: "Gender",
    placeholder: "",
    options: [
      {
        key: "male",
        value: "Male",
      },
      {
        key: "female",
        value: "Female",
      },
      // {
      //   key: "others",
      //   value: "others",
      // },
      {
        key: "nonbinary",
        value: "Nonbinary",
      },
      {
        key: "prefer not to answer",
        value: "Prefer not to answer",
      },
    ],
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    width: "col-span-6 sm:col-span-6 md:col-span-3 lg:col-span-2 xl:col-span-2",
  },
  {
    type: "input",
    name: "location",
    fieldtype: "text",
    label: "Location",
    placeholder: "Enter Location",
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    width: "col-span-6 sm:col-span-6 md:col-span-3 lg:col-span-2 xl:col-span-2",
  },
  {
    type: "input",
    name: "professional_bio",
    fieldtype: "text",
    label: "Professional Bio",
    placeholder: "Enter your bio",
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    width: "col-span-6",
  },
];

export const PARICIPANT_LABEL = {
  [INSIGHTS.MyInsights]: "Instructor",
  [INSIGHTS.MentorInsights]: "Mentor",
  [INSIGHTS.EventMangerInsights]: "Supervisor",
};

export const PARTICIPANT_ROLE = {
  [INSIGHTS.MyInsights]: "Participants",
  [INSIGHTS.MentorInsights]: "Mentees",
  [INSIGHTS.EventMangerInsights]: "Volunteers",
};
export const reportAdminColumns = (clickHandler, participantlabel = "") => [
  {
    field: "name",
    headerName: "Insights Name",
    flex: 1,
    id: 1,
    minWidth: 150,
    status: ["all", "approved", "rejected"],
    renderCell: (params) => {
      return (
        <span
          className="text-font-primary-main underline max-w-3 truncate cursor-pointer"
          onClick={() => clickHandler(params?.row)}
        >
          {params?.row?.name}
        </span>
      );
    },
  },
  {
    field: "created_by_first_name",
    headerName: `${PARICIPANT_LABEL[participantlabel]} First Name`,
    flex: 1,
    id: 1,
    minWidth: 150,
    status: ["approved", "rejected"],
  },
  // {
  //   field: "created_by_middle_name",
  //   headerName: `${MentorType} Middle Name`,
  //   flex: 1,
  //   id: 1,
  // minWidth:150,
  //   status: ["approved", "rejected"],
  // },
  {
    field: "created_by_last_name",
    headerName: `${PARICIPANT_LABEL[participantlabel]} Last Name`,
    flex: 1,
    id: 1,
    minWidth: 150,
    status: ["approved", "rejected"],
  },
  {
    field: "category_name",
    headerName: "Category",
    flex: 1,
    id: 1,
    minWidth: 150,
    status: ["all", "approved", "rejected"],
  },
  {
    field: "program_name",
    headerName: `${itemTitle} Name`,
    flex: 1,
    id: 1,
    minWidth: 150,
    status: ["all", "approved", "rejected"],
  },
  {
    field: "created_at",
    headerName: "Create Date",
    flex: 1,
    id: 1,
    minWidth: 150,
    renderCell: ({ row }) => dateFormat(row?.created_at),
    status: ["all"],
  },
  {
    field: "participates_count",
    headerName: `${PARTICIPANT_ROLE[participantlabel]}`,
    flex: 1,
    id: 1,
    minWidth: 150,
    status: ["all", "approved", "rejected"],
    headerAlign: "center",
    align: "center",
    cellClassName: "center-aligned-cell",
  },
  {
    field: "updated_at",
    headerName: "Last Updated Date",
    flex: 1,
    id: 1,
    minWidth: 150,
    renderCell: ({ row }) => dateFormat(row?.updated_at),
    status: ["approved", "rejected"],
  },
  {
    field: "updated_by_full_name",
    headerName: "Last Updated By",
    flex: 1,
    id: 1,
    minWidth: 150,
    status: ["approved", "rejected"],
  },

  // {
  //   field: 'designation',
  //   headerName: 'Designation',
  //   flex: 1,
  //   id: 1,
  // },
  // {
  //   field: 'designation',
  //   headerName: 'Designation',
  //   flex: 1,
  //   id: 1,
  // },
  // {
  //   field: 'designation',
  //   headerName: 'Designation',
  //   flex: 1,
  //   id: 1,
  // },
];

export const menteeTicketCreationField = [
  {
    type: "input",
    name: "subject",
    fieldtype: "text",
    label: "Ticket Subject",
    placeholder: "Ticket Subject",
    inputRules: {
      // required: "This field is required",
    },
    size: true,
    width: "col-span-1",
  },
  {
    type: "file",
    name: "attachments",
    fieldtype: "file",
    label: "Documents Upload",
    placeholder: "",
    inputRules: {
      // required: "This field is required",
    },
    size: false,
    width: "col-span-1",
  },
  {
    type: "textarea",
    name: "description",
    label: "Description",
    placeholder: "Enter Description",
    inputRules: {
      // required: "This field is required",
    },
    width: "w-full",
    for: ["admin"],
  },
];

const normalizeVehicleState = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const formatVehicleStateLabel = (value) => {
  const state = normalizeVehicleState(value);
  if (!state) return "-";
  if (state === "stop" || state === "stopped") return "Stopped";
  if (state === "moving") return "Moving";
  return state.charAt(0).toUpperCase() + state.slice(1);
};

const isVehicleStoppedState = (value) => {
  const state = normalizeVehicleState(value);
  return state === "stop" || state === "stopped";
};

export const EquipmentTableColumn = (handleClickFilterType) => {
  
  return [
    {
      field: "name",
      headerName: "Mobile Unit Name",
      id: 1,
      flex: 0.5,
      minWidth: 120,
    },
    {
      field: "location",
      headerName: "Location",
      id: 1,
      flex: 1,
      minWidth: 160,
      renderCell: (params) => {
        const row = params?.row || {};
        const cityFromDetails = [
          row.location_details?.city,
          row.location_details?.state_name,
        ]
          .filter(Boolean)
          .join(", ");
        const city =
          row.address ||
          cityFromDetails ||
          row.city ||
          "-";
        return <span className="truncate">{city}</span>;
      },
    },
    {
      field: "vehicle_state",
      headerName: "State",
      id: 1,
      flex: 0.5,
      minWidth: 220,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const state = normalizeVehicleState(params?.row?.vehicle_state);
        if (!state) return <span>-</span>;

        const isMoving = state === "moving";
        const isStopped = isVehicleStoppedState(state);
        const pillClass = isMoving
          ? "vehicle-state-pill--moving"
          : isStopped
            ? "vehicle-state-pill--stopped"
            : "vehicle-state-pill--other";

        return (
          <span className={`vehicle-state-pill ${pillClass}`}>
            <span
              className={`vehicle-state-dot${
                isMoving ? " vehicle-state-dot--live" : ""
              }`}
              aria-hidden
            />
            {formatVehicleStateLabel(params?.row?.vehicle_state)}
          </span>
        );
      },
    },
    {
      field: "speed",
      headerName: "Speed",
      id: 1,
      flex: 0.5,
      minWidth: 120,
      renderCell: (params) => {
        const speed = params?.row?.speed;
        if (speed === null || speed === undefined || speed === "") {
          return <span>-</span>;
        }
        const unit = params?.row?.speed_unit || "";
        const isStopped = isVehicleStoppedState(params?.row?.vehicle_state);
        return (
          <span className={isStopped ? "text-slate-400" : undefined}>
            {speed}
            {unit ? ` ${unit}` : ""}
          </span>
        );
      },
    },
    {
      field: "captured_at",
      headerName: "Captured At",
      id: 1,
      flex: 1,
      minWidth: 160,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => {
        const capturedAt =
          params?.row?.captured_at || params?.row?.recorded_at || null;
        const updatedAt = params?.row?.updated_at || null;

        if (!capturedAt && !updatedAt) {
          return <span>...</span>;
        }

        const capturedLabel = capturedAt
          ? formatWallClockDateTimeCell(capturedAt, { emptyValue: "..." })
          : "...";
        const updatedRelative = updatedAt
          ? formatCompactRelativeTimeAgo(updatedAt, { emptyValue: "" })
          : "";

        return (
          <div className="flex h-full w-full flex-col items-start justify-center text-left leading-tight">
            <span className="whitespace-nowrap">{capturedLabel}</span>
            {updatedRelative ? (
              <span className="text-[12px] text-gray-600 whitespace-nowrap">
                Updated {updatedRelative}
              </span>
            ) : null}
          </div>
        );
      },
    },
  ];
};

export const MobileUnitTableColumn = () => {
  return [
    {
      field: "name",
      headerName: "Mobile Unit Name",
      id: 1,
      minWidth: 200,
    },
    {
      field: "specialty",
      headerName: "Specialty",
      id: 1,
      minWidth: 200,
      renderCell: (params) => {
        const value = params?.row?.specialty;
        if (!value) return <span>N/A</span>;
        if (typeof value === "string") {
          return <span>{String(value)}</span>;
        }
        if (typeof value === "object" && value.length > 0) {
          return <span>{value.map((item) => item).join(", ")}</span>;
        }
        return <span>{value?.name ?? "N/A"}</span>;
      },
    },
    {
      field: "location",
      headerName: "City",
      id: 1,
      minWidth: 220,
      renderCell: (params) => (
        <span>{`${
          params?.row?.location_details?.city
            ? params?.row?.location_details?.city
            : "-"
        }${params?.row?.location_details?.state_name ? ", " : ""}${
          params?.row?.location_details?.state_name
            ? params?.row?.location_details?.state_name
            : ""
        }`}</span>
      ),
    },
    {
      field: "assigned_hta",
      headerName: "Assigned HTA",
      id: 1,
      minWidth: 250,
      renderCell: (params) => (
        <span>
          {params?.row?.assigned_hta
            ? params?.row?.hta_details?.full_name
            : "Unassigned"}
        </span>
      ),
    },
    {
      field: "updated_date",
      headerName: "Last Updated Date",
      id: 1,
      minWidth: 220,
      renderCell: (params) => (
        <span>
          {formatWallClockDateCell(params?.row?.updated_at, {
            emptyValue: "...",
          })}
        </span>
      ),
    },
  ];
};


